import {
  SmileIcon,
  CloudSunIcon,
  StarIcon,
  MessagesSquareIcon,
  ThermometerIcon,
  SchoolIcon,
  LayersIcon,
  ClipboardListIcon,
  GiftIcon,
  CalendarDaysIcon,
  TargetIcon,
  PrinterIcon,
  SlidersHorizontalIcon,
  BookOpenIcon,
  ListTreeIcon,
  FileTextIcon,
  MonitorSmartphoneIcon,
  PackageCheckIcon,
  SearchCheckIcon,
  CalendarRangeIcon,
  BabyIcon,
  DoorOpenIcon,
  NotebookPenIcon,
} from 'lucide-react'
import type { Bonus, Faq, IconItem, Stat, Testimonial, Volume } from '../types/landing'

export const brandName = 'SESIONES LISTAS™'
export const price = '$19.990 ARS'

export const heroBenefits: IconItem[] = [
  { icon: TargetIcon, title: 'Organizado por objetivo terapéutico' },
  { icon: PrinterIcon, title: 'Listo para imprimir o abrir' },
  { icon: SlidersHorizontalIcon, title: 'Adaptable a cada niño' },
  { icon: CalendarDaysIcon, title: 'Recursos para todo el año' },
]

export const stats: Stat[] = [
  { icon: PackageCheckIcon, label: 'Recursos listos para usar' },
  { value: '6', label: 'Tomos temáticos' },
  { value: '5', label: 'Bonos incluidos' },
  { value: '12', label: 'Meses de sesiones' },
]

export const beforeItems = [
  'Buscar actividades en internet antes de cada sesión',
  'Improvisar sin un recurso pensado para el objetivo',
  'Crear fichas y materiales desde cero',
  'Repetir siempre los mismos recursos',
  'Preguntarte cada día “¿qué actividad uso hoy?”',
]

export const afterItems = [
  'Recursos organizados por objetivo terapéutico',
  'Elegís en minutos qué usar',
  'Imprimís o abrís, y listo',
  'Variedad para todo el año',
  'Llegás a cada sesión con un plan',
]

// Cantidad total de actividades: pendiente hasta cerrar el contenido final.
export const pills = [
  { value: '[CANTIDAD]', label: 'Actividades' },
  { value: '6', label: 'Tomos' },
  { value: '5', label: 'Bonos' },
]

export const scenes = [
  'Terminás una sesión y ya estás pensando qué vas a hacer en la próxima',
  'Pasás horas buscando en internet una actividad que realmente sirva',
  'Llegás a la sesión con una idea, pero sin el material preparado',
  'Armás fichas desde cero de noche, después de un día entero de consultas',
  'Sentís que usás siempre los mismos recursos con todos los niños',
]

export const steps = [
  { title: 'Identificá el objetivo', text: 'Definí qué querés trabajar en la sesión.' },
  { title: 'Elegí el recurso', text: 'Buscalo en el tomo que corresponde a ese objetivo.' },
  { title: 'Imprimí o abrí', text: 'En papel o en pantalla, según tu modalidad.' },
  { title: 'Adaptalo al niño', text: 'Ajustalo a su edad, ritmo y momento.' },
  { title: 'Usalo en sesión', text: 'Llegás con el material listo para trabajar.' },
]

export const packContents: IconItem[] = [
  { icon: LayersIcon, title: '6 Tomos temáticos', text: 'Organizados por objetivo terapéutico' },
  { icon: ClipboardListIcon, title: '[CANTIDAD DE ACTIVIDADES] actividades', text: 'Listas para imprimir o abrir' },
  { icon: GiftIcon, title: '5 Bonos exclusivos', text: 'Valorados en más de $70.000' },
  { icon: CalendarDaysIcon, title: 'Todo el año cubierto', text: 'Recursos para cada etapa del proceso' },
]

// Cantidad de actividades y tipos de recursos por tomo: pendientes hasta cerrar el contenido final.
export const volumes: Volume[] = [
  {
    number: 1,
    title: 'Emociones',
    description: 'Recursos para identificar, nombrar y expresar emociones en sesión.',
    includes: ['[CANTIDAD] actividades', '[TIPO DE RECURSO]', '[TIPO DE RECURSO]'],
    color: '#F4D3C1',
    deep: '#9A4F31',
    icon: SmileIcon,
  },
  {
    number: 2,
    title: 'Ansiedad y miedos',
    description: 'Recursos para abordar preocupaciones y miedos de forma lúdica.',
    includes: ['[CANTIDAD] actividades', '[TIPO DE RECURSO]', '[TIPO DE RECURSO]'],
    color: '#DAD4EC',
    deep: '#564B8C',
    icon: CloudSunIcon,
  },
  {
    number: 3,
    title: 'Autoestima',
    description: 'Recursos para trabajar la imagen de sí mismo y el reconocimiento propio.',
    includes: ['[CANTIDAD] actividades', '[TIPO DE RECURSO]', '[TIPO DE RECURSO]'],
    color: '#F1E2B4',
    deep: '#7A5D10',
    icon: StarIcon,
  },
  {
    number: 4,
    title: 'Habilidades sociales',
    description: 'Recursos para trabajar el vínculo, la comunicación y el diálogo.',
    includes: ['[CANTIDAD] actividades', '[TIPO DE RECURSO]', '[TIPO DE RECURSO]'],
    color: '#CFE1D3',
    deep: '#3A6447',
    icon: MessagesSquareIcon,
  },
  {
    number: 5,
    title: 'Enojo y autocontrol',
    description: 'Recursos para trabajar la regulación y la expresión del enojo.',
    includes: ['[CANTIDAD] actividades', '[TIPO DE RECURSO]', '[TIPO DE RECURSO]'],
    color: '#EEC6C1',
    deep: '#8E423C',
    icon: ThermometerIcon,
  },
  {
    number: 6,
    title: 'Escuela y familia',
    description: 'Recursos para abordar situaciones del entorno escolar y familiar.',
    includes: ['[CANTIDAD] actividades', '[TIPO DE RECURSO]', '[TIPO DE RECURSO]'],
    color: '#CDDDEA',
    deep: '#365E7C',
    icon: SchoolIcon,
  },
]

export const instantItems: IconItem[] = [
  { icon: BookOpenIcon, title: 'Los 6 tomos temáticos' },
  { icon: GiftIcon, title: 'Los 5 bonos exclusivos' },
  { icon: ListTreeIcon, title: 'Índice por objetivo terapéutico' },
  { icon: FileTextIcon, title: 'Archivos PDF digitales' },
  { icon: MonitorSmartphoneIcon, title: 'Recursos para imprimir o utilizar directamente en pantalla' },
  { icon: CalendarDaysIcon, title: 'Material para todo el año de sesiones' },
]

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: 'Selector Express de Actividades',
    description: 'Una guía rápida para encontrar qué recurso usar según el objetivo que quieras trabajar en sesión.',
    value: '$14.990',
    color: '#E6EFEC',
    deep: '#2F5F5B',
    icon: SearchCheckIcon,
  },
  {
    number: 2,
    title: 'Planificador Anual de Sesiones',
    description: 'Organizá tus objetivos, recursos y sesiones a lo largo del año sin empezar cada semana desde cero.',
    value: '$16.990',
    color: '#F6E3D6',
    deep: '#934A2A',
    icon: CalendarRangeIcon,
  },
  {
    number: 3,
    title: 'Guía de Adaptación por Edad',
    description: 'Ideas prácticas para adaptar las actividades según la edad, comprensión, participación y ritmo de cada niño.',
    value: '$12.990',
    color: '#E9E5F3',
    deep: '#564B8C',
    icon: BabyIcon,
  },
  {
    number: 4,
    title: 'Banco de Aperturas y Cierres',
    description: 'Recursos rápidos para comenzar y terminar tus sesiones con mayor variedad y estructura.',
    value: '$11.990',
    color: '#E3ECF3',
    deep: '#365E7C',
    icon: DoorOpenIcon,
  },
  {
    number: 5,
    title: 'Registro Profesional de Sesiones',
    description: 'Una herramienta práctica para organizar observaciones, objetivos trabajados, recursos utilizados y próximos pasos.',
    value: '$13.990',
    color: '#F5EBCB',
    deep: '#7A5D10',
    icon: NotebookPenIcon,
  },
]

export const offerRows = [
  { label: 'SESIONES LISTAS™', detail: '6 tomos temáticos completos', value: 'Incluido' },
  { label: '5 bonos exclusivos', detail: 'Valorados en más de $70.000', value: '+$70.000' },
]

/*
 * TESTIMONIO DEMO — REEMPLAZAR ANTES DE PUBLICAR
 * Textos y nombres ficticios (DEMO), usados solo como placeholders visuales.
 */
export const testimonials: Testimonial[] = [
  {
    quote:
      'Lo que más me gustó fue poder entrar al material y encontrar rápido una actividad según lo que quería trabajar. Me simplificó muchísimo la preparación.',
    name: 'Carolina M.', // DEMO
    role: 'Psicóloga infantil · Rosario', // DEMO
    isDemo: true,
  },
  {
    quote:
      'Antes tenía varias carpetas y recursos sueltos. Tener todo organizado por objetivos me resultó mucho más práctico.',
    name: 'Julieta R.', // DEMO
    role: 'Terapeuta infantil · CABA', // DEMO
    isDemo: true,
  },
  {
    quote: 'Lo estoy usando tanto en sesiones presenciales como online y me ayuda mucho a variar las propuestas.',
    name: 'Florencia G.', // DEMO
    role: 'Psicóloga infantil · Córdoba', // DEMO
    isDemo: true,
  },
  {
    quote: 'Me gustó especialmente que no tengo que crear una ficha desde cero cada vez que quiero trabajar un tema.',
    name: 'Mariana S.', // DEMO
    role: 'Psicopedagoga · Mendoza', // DEMO
    isDemo: true,
  },
  {
    quote: 'El índice por objetivos hace que encontrar una actividad sea muchísimo más rápido.',
    name: 'Paula D.', // DEMO
    role: 'Psicóloga infantil · La Plata', // DEMO
    isDemo: true,
  },
  {
    quote: 'Es de esos recursos que terminás consultando todas las semanas.',
    name: 'Valeria T.', // DEMO
    role: 'Terapeuta infantil · Neuquén', // DEMO
    isDemo: true,
  },
]

export const faqs: Faq[] = [
  {
    question: '¿Cómo recibo el material?',
    answer:
      'Después de confirmar tu compra vas a recibir acceso digital al material para descargarlo y utilizarlo desde tus dispositivos.',
  },
  {
    question: '¿Para qué edades sirven los recursos?',
    answer:
      'Los recursos están pensados inicialmente para niños de 4 a 12 años. Cada propuesta puede adaptarse según la edad, nivel de comprensión, ritmo y necesidades particulares de cada niño.',
  },
  {
    question: '¿Sirve si recién empiezo a atender niños?',
    answer:
      'Sí. La organización por objetivos está pensada justamente para ayudarte a encontrar recursos sin tener que crear todo desde cero. Siempre deberás adaptar cada propuesta según tu criterio profesional y cada caso.',
  },
  {
    question: '¿Puedo usarlo en sesiones online?',
    answer:
      'Sí. Muchos de los recursos pueden utilizarse directamente desde una computadora o tablet, compartiéndolos en pantalla, sin necesidad de imprimirlos.',
  },
  {
    question: '¿Los bonos están incluidos en el precio?',
    answer: 'Sí. Los 5 bonos están incluidos con tu compra de SESIONES LISTAS™ sin costo adicional.',
  },
  {
    question: '¿En qué formato están los archivos?',
    answer:
      'Los materiales se entregan en formato PDF digital. Podés descargarlos, consultarlos desde cualquier dispositivo o imprimirlos cuando lo necesites.',
  },
  {
    question: '¿Tengo que pagar una mensualidad?',
    answer: 'No. SESIONES LISTAS™ se compra mediante un único pago de $19.990 ARS.',
  },
  {
    question: '¿Qué pasa si no es lo que esperaba?',
    answer:
      'Contás con una garantía de 7 días desde la compra. Si el material no cumple con tus expectativas, podés solicitar el reembolso según nuestra política de devoluciones.',
  },
]

export const worksheetsImage =
  'https://cdn.magicpatterns.com/patterns/generated-images/f97d40f1-39c9-4a48-a03c-905e84a34bd0.jpg'
